import React, { useMemo } from 'react';
import useStyles from './styles';
import { useAdSpaceVisibility } from './useAdSpaceVisibility';
import { AdSlot } from 'atoms';
import { getAdConfig } from 'config/Ads';
import { ROUTES } from 'config/Nav';
import { AdSpaceType } from 'types/advertising';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Link } from '@elevator/ui';

/**
 * Helper function to get the ad unit prefix based on the environment.
 * Appends 'test-' to ad unit names in non-production environments.
 *
 * @returns The prefix string ('test-' or empty string)
 */
const getAdUnitPrefix = (): string => {
  return process.env.COOKIE_DOMAIN === 'elevatormag.com' ? '' : 'test-';
};

/**
 * AdSpace Component
 *
 * A wrapper component for displaying Google Publisher Tag (GPT) ad slots with responsive sizing.
 * Supports both responsive mode (shows different ad sizes based on viewport) and fixed size mode.
 *
 * In responsive mode (breakpoint 970px, same as CSS in `styles.ts`):
 * - `banner-ad`: 300×250 below 970px, 970×250 at larger viewports
 * - `layout-top-banner`: 320×50 below 970px, 970×250 at larger viewports
 *
 * Automatically prefixes ad units with 'test-' in non-production environments.
 *
 * Root is not an `aside` so slots can sit inside sidebars or other `aside` regions without nested complementary landmarks.
 *
 * @param props - Component props
 * @param props.type - The type of ad space (determines ad configuration)
 * @param props.adUnit - The ad unit path/name
 * @param props.size - Ad size mode: 'responsive' (default), 'lg', or 'md'
 * @param props.slotId - Optional custom slot ID. In responsive mode, '-md' or '-lg' suffix is appended
 */
const AdSpace = ({ size = 'responsive', type, adUnit, slotId }: AdSpacePropTypes) => {
  const { classes } = useStyles();
  const root = classes.root;
  const slotKeys = useMemo(
    () => (size === 'responsive' ? (['md', 'lg'] as const) : (['single'] as const)),
    [size],
  );
  const { showAdSpace, handleSlotFilled } = useAdSpaceVisibility([...slotKeys]);

  // Memoize ad config and prefixed ad unit to avoid recalculation on re-renders
  const adConfig = useMemo(() => getAdConfig(type), [type]);

  const prefixedAdUnit = useMemo(() => {
    const prefix = getAdUnitPrefix();
    return prefix + adUnit;
  }, [adUnit]);

  // Error handling: if adConfig is missing, return null to prevent runtime errors
  if (!adConfig) {
    if (process.env.NODE_ENV !== 'production') {
      console.error(
        `AdSpace: No ad configuration found for type "${type}". ` +
          `Please check that the ad type is valid and getAdConfig returns a valid config.`,
      );
    }
    return null;
  }

  const renderAdLabel = () => (
    <div className={classes.adLabelContainer}>
      <Link href={ROUTES.ADVERTISE.to}>
        <Typography
          variant="overline1"
          color="text.secondary"
          className={classes.adLabel}
        >
          ADVERTISEMENT
        </Typography>
      </Link>
    </div>
  );

  // Slots always mount so GPT can request ads; label and layout show only after a fill.
  if (size === 'responsive') {
    return (
      <Box
        className={root}
        data-component="AdSpace"
        aria-hidden={showAdSpace ? undefined : true}
      >
        <div className={classes.mediumAd}>
          <AdSlot
            sizes={[adConfig.sizes.md]}
            sizeMapping={[adConfig.sizeMapping.md]}
            adUnit={prefixedAdUnit}
            slotId={slotId ? `${slotId}-md` : undefined}
            showAdUnit
            onAdFilled={handleSlotFilled('md')}
          />
        </div>
        <div className={classes.largeAd}>
          <AdSlot
            sizes={[adConfig.sizes.lg]}
            sizeMapping={[adConfig.sizeMapping.lg]}
            adUnit={prefixedAdUnit}
            slotId={slotId ? `${slotId}-lg` : undefined}
            showAdUnit
            onAdFilled={handleSlotFilled('lg')}
          />
        </div>
        {showAdSpace ? renderAdLabel() : null}
      </Box>
    );
  }

  return (
    <Box
      className={root}
      data-component="AdSpace"
      aria-hidden={showAdSpace ? undefined : true}
    >
      <AdSlot
        sizes={[adConfig.sizes[size]]}
        sizeMapping={[adConfig.sizeMapping[size]]}
        adUnit={prefixedAdUnit}
        slotId={slotId}
        showAdUnit
        onAdFilled={handleSlotFilled('single')}
      />
      {showAdSpace ? renderAdLabel() : null}
    </Box>
  );
};

/**
 * Props for the AdSpace component
 */
export type AdSpacePropTypes = {
  /** The type of ad space (determines ad configuration) */
  type: AdSpaceType;
  /** The ad unit path/name */
  adUnit: string;
  /** Ad size mode: 'responsive' shows different sizes based on viewport, 'lg' or 'md' shows fixed size */
  size?: 'responsive' | 'lg' | 'md';
  /** Optional custom slot ID. In responsive mode, '-md' or '-lg' suffix is automatically appended */
  slotId?: string;
};

export default AdSpace;
